// ============================================
// CV PDF İNDİRME KOMPONENTI
// jsPDF ile tarayıcıda PDF üretir
// ============================================

'use client';

import { useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { useTranslation } from '@crew-at-sea/i18n';
import { useAuthStore } from '@/store/auth';

interface CvPdfButtonProps {
  profile: any;
  certificates?: any[];
  seaServiceRecords?: any[];
  references?: any[];
  className?: string;
}

// Departman etiketi
function deptLabel(dept: string, lang: string): string {
  const map: Record<string, { tr: string; en: string }> = {
    DECK: { tr: 'Güverte', en: 'Deck' },
    ENGINE: { tr: 'Makine', en: 'Engine' },
    ELECTRICAL: { tr: 'Elektrik', en: 'Electrical' },
    CATERING: { tr: 'Catering', en: 'Catering' },
  };
  return map[dept]?.[lang === 'tr' ? 'tr' : 'en'] || dept;
}

// Tarih formatla
function fmtDate(dateStr: string | null | undefined, lang: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function CvPdfButton({ profile, certificates = [], seaServiceRecords = [], references = [], className = '' }: CvPdfButtonProps) {
  const { t, i18n } = useTranslation(['panel', 'common']);
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const lang = i18n.language || 'tr';

  const handleDownload = async () => {
    setLoading(true);
    try {
      // Dinamik import - bundle boyutunu küçük tut
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 18;
      const colW = pageW - margin * 2;
      let y = 20;

      // ── RENK PALETİ ──
      const primary: [number, number, number] = [5, 132, 199];     // #0584c7
      const dark: [number, number, number] = [30, 41, 59];
      const gray: [number, number, number] = [100, 116, 139];
      const light: [number, number, number] = [241, 245, 249];

      // ── HEADER ──
      doc.setFillColor(...primary);
      doc.rect(0, 0, pageW, 42, 'F');

      const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(fullName, margin, 18);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const rankName = profile.rank?.nameTr || profile.rank?.nameEn || profile.currentRank?.nameTr || '';
      const deptName = profile.department ? deptLabel(profile.department, lang) : '';
      const subtitle = [rankName, deptName].filter(Boolean).join(' • ');
      if (subtitle) doc.text(subtitle, margin, 27);

      // İletişim satırı
      const contactParts: string[] = [];
      if (user?.email) contactParts.push(user.email);
      if (profile.phone) contactParts.push(profile.phone);
      if (profile.city || profile.country) contactParts.push([profile.city, profile.country].filter(Boolean).join(', '));
      if (contactParts.length) {
        doc.setFontSize(9);
        doc.setTextColor(200, 230, 255);
        doc.text(contactParts.join('  |  '), margin, 37);
      }

      y = 52;

      // ── BÖLÜM BAŞLIĞI FONKSİYONU ──
      const sectionTitle = (title: string) => {
        doc.setFillColor(...light);
        doc.rect(margin, y, colW, 7, 'F');
        doc.setDrawColor(...primary);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin, y + 7);
        doc.setTextColor(...dark);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(title.toUpperCase(), margin + 3, y + 5);
        y += 10;
      };

      // ── HAKKIMDA ──
      if (profile.bio) {
        sectionTitle(lang === 'tr' ? 'Hakkımda' : 'About');
        doc.setTextColor(...dark);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(profile.bio, colW);
        doc.text(lines, margin, y);
        y += lines.length * 4.5 + 6;
      }

      // ── KİŞİSEL BİLGİLER ──
      sectionTitle(lang === 'tr' ? 'Kişisel Bilgiler' : 'Personal Information');
      const personalRows: [string, string][] = [];
      if (profile.nationality) personalRows.push([lang === 'tr' ? 'Uyruk' : 'Nationality', profile.nationality]);
      if (profile.dateOfBirth) personalRows.push([lang === 'tr' ? 'Doğum Tarihi' : 'Date of Birth', fmtDate(profile.dateOfBirth, lang)]);
      if (profile.gender) personalRows.push([lang === 'tr' ? 'Cinsiyet' : 'Gender', profile.gender === 'MALE' ? (lang === 'tr' ? 'Erkek' : 'Male') : (lang === 'tr' ? 'Kadın' : 'Female')]);
      if (profile.experienceYears) personalRows.push([lang === 'tr' ? 'Deneyim' : 'Experience', `${profile.experienceYears} ${lang === 'tr' ? 'yıl' : 'years'}`]);

      if (personalRows.length) {
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [],
          body: personalRows,
          styles: { fontSize: 9, cellPadding: 2.5, textColor: dark },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45, textColor: gray }, 1: { textColor: dark } },
          tableLineColor: [226, 232, 240],
          tableLineWidth: 0.1,
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // ── EĞİTİM BİLGİLERİ ──
      if (profile.educationSchool) {
        sectionTitle(lang === 'tr' ? 'Eğitim' : 'Education');
        const eduRows: [string, string][] = [];
        eduRows.push([lang === 'tr' ? 'Okul' : 'School', profile.educationSchool]);
        if (profile.educationDepartment) eduRows.push([lang === 'tr' ? 'Bölüm' : 'Department', profile.educationDepartment]);
        if (profile.educationDegree) eduRows.push([lang === 'tr' ? 'Derece' : 'Degree', profile.educationDegree]);
        if (profile.educationGraduationYear) eduRows.push([lang === 'tr' ? 'Mezuniyet' : 'Graduation', String(profile.educationGraduationYear)]);

        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [],
          body: eduRows,
          styles: { fontSize: 9, cellPadding: 2.5 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45, textColor: gray }, 1: { textColor: dark } },
          tableLineColor: [226, 232, 240],
          tableLineWidth: 0.1,
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // ── DENİZ HİZMETİ ──
      const records = Array.isArray(seaServiceRecords) ? seaServiceRecords : [];
      if (records.length > 0) {
        sectionTitle(lang === 'tr' ? 'Deniz Hizmeti' : 'Sea Service');
        const seaBody = records.map((r: any) => [
          r.vesselName || '-',
          r.vesselType || '-',
          r.rank?.nameTr || r.rank?.nameEn || '-',
          fmtDate(r.signOnDate, lang),
          r.signOffDate ? fmtDate(r.signOffDate, lang) : (lang === 'tr' ? 'Devam' : 'Present'),
          r.seaDays ? `${r.seaDays} ${lang === 'tr' ? 'gün' : 'days'}` : '-',
        ]);
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [[
            lang === 'tr' ? 'Gemi' : 'Vessel',
            lang === 'tr' ? 'Tür' : 'Type',
            lang === 'tr' ? 'Rütbe' : 'Rank',
            lang === 'tr' ? 'Başlangıç' : 'Sign On',
            lang === 'tr' ? 'Bitiş' : 'Sign Off',
            lang === 'tr' ? 'Gün' : 'Days',
          ]],
          body: seaBody,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // ── SERTİFİKALAR ──
      const certs = Array.isArray(certificates) ? certificates : [];
      if (certs.length > 0) {
        sectionTitle(lang === 'tr' ? 'Sertifikalar' : 'Certificates');
        const certBody = certs.map((c: any) => [
          c.certificateType?.nameTr || c.certificateType?.nameEn || c.name || '-',
          c.certificateNumber || '-',
          c.issueDate ? fmtDate(c.issueDate, lang) : '-',
          c.expiryDate ? fmtDate(c.expiryDate, lang) : (lang === 'tr' ? 'Süresiz' : 'Lifetime'),
          c.isVerified ? (lang === 'tr' ? 'Doğrulandı' : 'Verified') : '-',
        ]);
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [[
            lang === 'tr' ? 'Sertifika' : 'Certificate',
            lang === 'tr' ? 'No' : 'No',
            lang === 'tr' ? 'Tarih' : 'Issue Date',
            lang === 'tr' ? 'Son Geçerlilik' : 'Expiry',
            lang === 'tr' ? 'Durum' : 'Status',
          ]],
          body: certBody,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // ── REFERANSLAR ──
      const refs = Array.isArray(references) ? references : [];
      if (refs.length > 0) {
        sectionTitle(lang === 'tr' ? 'Referanslar' : 'References');
        const refBody = refs.map((r: any) => [
          r.fullName || '-',
          r.position || '-',
          r.company || '-',
          r.phone || '-',
          r.email || '-',
        ]);
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [[
            lang === 'tr' ? 'Ad Soyad' : 'Name',
            lang === 'tr' ? 'Pozisyon' : 'Position',
            lang === 'tr' ? 'Şirket' : 'Company',
            lang === 'tr' ? 'Telefon' : 'Phone',
            lang === 'tr' ? 'E-posta' : 'Email',
          ]],
          body: refBody,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
      }

      // ── FOOTER ──
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        doc.text(
          `Crew At Sea  •  crewatsea.com  •  ${new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}`,
          margin,
          doc.internal.pageSize.getHeight() - 8,
        );
        doc.text(
          `${i} / ${pageCount}`,
          pageW - margin,
          doc.internal.pageSize.getHeight() - 8,
          { align: 'right' },
        );
      }

      // İndir
      const fileName = `${fullName.replace(/\s+/g, '_')}_CV.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={loading}
      className={`btn-secondary flex items-center justify-center gap-1.5 md:gap-2 ${className}`}
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" />
      ) : (
        <Download className="w-3.5 h-3.5 md:w-4 md:h-4" />
      )}
      <span className="truncate">
        {loading ? (
          t('common:loading')
        ) : (
          <>
            <span className="hidden sm:inline">CV </span>
            {lang === 'tr' ? 'İndir' : 'Download'}
          </>
        )}
      </span>
    </button>
  );
}
